import { useDraggable } from '@dnd-kit/core';
import { Box, Typography } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { PropsWithChildren } from 'react';
import { useDashboardCapabilities } from './dashboardCapabilities';
import { WidgetOptions } from './dashboardTypes';

interface DraggableComponentProps {
  id: string;
  type: string;
  title: string;
  dataKey: string;
  minH?: number;
  minW?: number;
  style?: string;
  options?: WidgetOptions;
  sx?: object;
}

const DraggableComponent = ({
  children,
  id,
  type,
  minH = 2,
  minW = 2,
  title,
  dataKey,
  style,
  options,
  sx,
}: PropsWithChildren<DraggableComponentProps>) => {
  const { canEditDashboardLayout } = useDashboardCapabilities();

  const display = (
    <Box
      display="flex"
      flexDirection="column"
      overflow="hidden"
      width="100%"
      sx={{ borderRadius: 1, border: 1, borderColor: 'divider', bgcolor: 'background.paper' }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="flex-start"
        sx={(theme) => ({
          px: 2,
          py: 1,
          color: theme.vars?.palette?.text?.primary,
          '&:hover': { bgcolor: theme.vars?.palette?.action?.hover },
        })}
      >
        <Typography textAlign="left" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
      </Box>
      <Box flex="1" overflow="hidden" display="flex">
        {children}
      </Box>
    </Box>
  );

  const dataDisplay = (
    <Box
      display="flex"
      alignItems="center"
      overflow="hidden"
      width="100%"
      sx={(theme) => ({
        borderRadius: 1,
        border: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        px: 2,
        py: 1,
        color: theme.vars?.palette?.text?.primary,
        ...(canEditDashboardLayout && { '&:hover': { bgcolor: theme.vars?.palette?.action?.hover } }),
      })}
    >
      <DragIndicatorIcon sx={{ mr: 1, fontSize: '1.2rem', opacity: canEditDashboardLayout ? 0.6 : 0.35 }} />
      <Typography textAlign="left" sx={{ fontWeight: 600, flexGrow: 1 }}>
        {title}
      </Typography>
    </Box>
  );

  const { attributes, listeners, setNodeRef } = useDraggable({
    id: id,
    data: { type, dataKey, display, minH, minW, style, options },
    disabled: !canEditDashboardLayout,
  });

  return (
    <Box
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      width="230px"
      aria-disabled={!canEditDashboardLayout}
      sx={{
        borderRadius: 1,
        m: 1,
        ...sx,
        ...(canEditDashboardLayout ? { cursor: 'grab', opacity: 1 } : { cursor: 'not-allowed', opacity: 0.45 }),
      }}
    >
      {dataDisplay}
    </Box>
  );
};

export default DraggableComponent;
